import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { usePlanActual } from "../lib/planes";
import {
  Calendar,
  AlertTriangle,
  UserPlus,
  MessageCircle,
  ArrowDown,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Line,
  LineChart,
  CartesianGrid,
} from "recharts";

const PIE_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#6366F1", "#EF4444", "#0EA5E9"];

// 🔴 De esta función se retiraron CINCO cifras fabricadas (auditoría de copy, 12 Sep 2026).
// Se documentan aquí, no en el historial de git, porque la trampa es reintroducirlas sin saber:
//
//   · `weekly_trend` se construía repartiendo el total del mes entre 7 días con un factor
//     ALEATORIO, y el comentario que lo acompañaba lo admitía en inglés. La gráfica
//     "Citas creadas (últimos 7 días)" dibujaba una curva DISTINTA en cada carga. Ahora sale
//     de GET /metrics/appointments-by-day?days=7, que ya existía y cuenta citas reales.
//   · `avg_response_time_sec: 28` — hardcodeado. No se mide en ningún lado: los timestamps de
//     los mensajes reales viven en `whatsapp_messages`, en agency, no en este repo. RETIRADA.
//   · `ai_created_appointments` — contaba `status='Completada'`, un estado que NO EXISTE.
//     Medido contra las 9 clínicas (65 citas): Pendiente/Atendido/Confirmado/No asistió/
//     Cancelado. Daba SIEMPRE 0. No era una métrica en cero, era una consulta rota. RETIRADA
//     hasta que `appointments` tenga columna de origen — ver el pendiente en CLAUDE.md.
//   · `patients_recovered` / `revenue_recovered_mxn` — salían del flag `recovered`, cuyo único
//     escritor era el botón "Enviar" de Recuperación, que marcaba al paciente como recuperado
//     SIN contactarlo. Retirado ese botón, el flag no tiene escritor. RETIRADAS.
//   · `opportunity_mxn: total_patients * 500` — un valor por paciente inventado. RETIRADA.
//
// Y la tasa de no-show, que SÍ tenía dato real detrás, estaba MAL: dividía pacientes con
// status 'No asistió' entre CITAS del mes — numerador de una tabla, denominador de otra.
// Ahora el backend devuelve las dos mitades, citas / citas, con la MISMA ventana.
function adaptAnalytics(raw, byDay) {
  const leads_by_source = {};
  (raw.by_source || []).forEach(({ source, count }) => { leads_by_source[source] = count; });

  const statusMap = {};
  (raw.by_status || []).forEach(({ status, count }) => { statusMap[status] = count; });
  const funnel = {
    nuevo:     statusMap["Nuevo"]       || 0,
    interesado:statusMap["Interesado"]  || 0,
    agendado:  (statusMap["Agendado"] || 0) + (statusMap["Confirmado"] || 0),
    confirmado:statusMap["Confirmado"]  || 0,
    atendido:  statusMap["Atendido"]    || 0,
  };

  const total_appointments = raw.appts_this_month || 0;
  const no_show_count = raw.appts_no_show_this_month || 0;

  return {
    ...raw,
    leads_by_source,
    funnel,
    // Citas reales por día, no una curva inventada. Puede venir vacío (clínica sin citas en
    // la ventana) y eso se pinta como "sin datos", nunca como una línea plana en cero.
    weekly_trend: (byDay || []).map((d) => ({ day: d.date, appointments: Number(d.count) })),
    total_appointments,
    no_show_count,
    // 🔴 Si no hubo NINGUNA cita en el periodo, la tasa es `null`, no 0. Un "0% de no-show"
    // con cero citas es otro número que miente: sugiere un mes perfecto donde no hubo mes.
    no_show_rate: total_appointments > 0
      ? Math.round((no_show_count / total_appointments) * 100)
      : null,
  };
}

export default function Analytics() {
  const { sinSofi } = usePlanActual(); // TEN.4
  const [a, setA] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    // Dos peticiones: el resumen + las citas reales por día de los últimos 7.
    // /metrics/appointments-by-day ya existía y cuenta `appointments` de verdad; es lo que
    // sustituye a la curva que se generaba con un factor aleatorio.
    Promise.all([
      api.get("/analytics/summary"),
      api.get("/metrics/appointments-by-day?days=7"),
    ])
      .then(([sum, byDay]) => setA(adaptAnalytics(sum.data, byDay.data)))
      .catch(() => setError("No se pudieron cargar los datos de analítica"));
  }, []);
  if (error) return <div className="p-8 text-rose-500">{error}</div>;
  if (!a) return <div className="p-8 text-slate-500">Cargando analítica…</div>;

  const sourceData = Object.entries(a.leads_by_source).map(([name, value]) => ({ name, value }));
  const funnelEntries = [
    { name: "Nuevo lead", value: a.funnel.nuevo },
    { name: "Interesado", value: a.funnel.interesado },
    { name: "Agendado", value: a.funnel.agendado },
    { name: "Confirmado", value: a.funnel.confirmado },
    { name: "Atendido", value: a.funnel.atendido },
  ];
  const maxFunnel = Math.max(...funnelEntries.map((f) => f.value), 1);

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full">
      <PageHeader title="Analítica" subtitle={sinSofi
        ? "Mide la actividad de tu clínica."
        : "Mide qué tanto está aportando Sofi al crecimiento de tu clínica."} />

      {/* Cuatro KPIs, los cuatro con dato real detrás y con su periodo dicho en la etiqueta.
          Los cinco que se fueron están documentados arriba, en adaptAnalytics(). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Calendar} label="Citas este mes" value={a.total_appointments} color="text-brand-electric" bg="bg-blue-50" />
        {/* 🔴 Sin citas en el mes no se pinta un 0%: se dice que no hay con qué calcularlo. */}
        <Kpi
          icon={AlertTriangle}
          label="Tasa de no-show"
          value={a.no_show_rate === null ? "—" : `${a.no_show_rate}%`}
          sub={a.no_show_rate === null ? "Sin citas este mes" : `${a.no_show_count} de ${a.total_appointments} citas este mes`}
          color="text-amber-600"
          bg="bg-amber-50"
        />
        <Kpi icon={UserPlus} label="Pacientes nuevos" value={a.new_leads} sub="Con estado “Nuevo”" color="text-indigo-600" bg="bg-indigo-50" />
        {!sinSofi && <Kpi icon={MessageCircle} label="Conversaciones" value={a.total_convos} sub="Total histórico" color="text-emerald-600" bg="bg-emerald-50" />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Weekly chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <div className="font-display font-bold">Citas por día (últimos 7 días)</div>
          <div className="text-xs text-slate-500 mt-0.5">Citas reales registradas en tu agenda.</div>
          {a.weekly_trend.length === 0 ? (
            <div className="h-64 mt-3 grid place-items-center text-sm text-slate-400" data-testid="analytics-weekly-empty">
              Sin citas en los últimos 7 días.
            </div>
          ) : (
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={a.weekly_trend.map((w) => ({ ...w, day: new Date(w.day).toLocaleDateString("es-MX", { weekday: "short" }) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="appointments" stroke="#2563EB" strokeWidth={3} dot={{ r: 4, fill: "#2563EB" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>

        {/* Leads by source */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
          <div className="font-display font-bold">Leads por fuente</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3}>
                  {sourceData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Funnel */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
        <div className="font-display font-bold">Embudo de conversión</div>
        <div className="text-xs text-slate-500 mt-0.5">Nuevo lead → Interesado → Agendado → Confirmado → Atendido</div>
        <div className="mt-5 space-y-3">
          {funnelEntries.map((f, i) => (
            <div key={f.name} className="flex items-center gap-3">
              <div className="w-32 text-sm font-medium text-slate-700">{f.name}</div>
              <div className="flex-1 h-9 rounded-lg bg-slate-100 overflow-hidden relative">
                <div
                  className="h-full rounded-lg flex items-center justify-end px-3 text-white text-xs font-semibold transition-all"
                  style={{
                    width: `${(f.value / maxFunnel) * 100}%`,
                    background: `linear-gradient(90deg, #2563EB, #${i === 4 ? "10B981" : "60A5FA"})`,
                    minWidth: 50,
                  }}
                >
                  {f.value}
                </div>
              </div>
              {i < funnelEntries.length - 1 && (
                <ArrowDown className="w-4 h-4 text-slate-400" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bar chart sources */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
        <div className="font-display font-bold">Comparativo por fuente</div>
        <div className="h-64 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sourceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="value" fill="#2563EB" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color, bg }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
      <div className={`w-10 h-10 rounded-xl ${bg} ${color} grid place-items-center`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-display text-3xl font-extrabold tracking-tighter mt-4">{value ?? "—"}</div>
      <div className="text-sm text-slate-600">{label}</div>
      <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </div>
  );
}
